/**
 * /bookings/:id — full-page booking detail wrapper.
 *
 * Accepts either an individual booking ID (from Today/floor-plan links) or a
 * booking group ID (from future deep-links). Strategy:
 *   1. Try GET /api/bookings/group/:id — if it returns data it's a group ID.
 *   2. On 404, fall back to GET /api/bookings/:id to get the individual booking
 *      row, then use its bookingGroupId to load the group.
 *
 * If the individual booking has no bookingGroupId (standalone booking), we
 * synthesise a single-member group so BookingDetail can render it.
 *
 * Hash-based section activation: #messages scrolls to / highlights the
 * messages panel after load.
 */
import { getBooking, getBookingGroup } from '@/lib/api';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BookingDetail } from '../components/BookingDetail';
import { SeriesChip } from './recurring/SeriesChip';

type GroupDetail = { groupId: string; bookings: unknown[] } | null;

/** Pull recurring_series_id off whichever entry shape the group endpoint returned. */
function seriesIdFromGroup(entries: unknown[]): string | null {
  for (const e of entries) {
    const row = (e as { booking?: { recurringSeriesId?: string | null } } | null)?.booking;
    if (row?.recurringSeriesId) return row.recurringSeriesId;
    const flat = (e as { recurringSeriesId?: string | null } | null)?.recurringSeriesId;
    if (flat) return flat;
  }
  return null;
}

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [groupId, setGroupId] = useState<string | null>(null);
  const [groupDetail, setGroupDetail] = useState<GroupDetail>(null);
  // Migration 0096 — recurring daycare series the booking belongs to (if any).
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether we need to scroll to messages after load.
  const shouldScrollMessages = useRef(
    typeof window !== 'undefined' && window.location.hash === '#messages',
  );
  const messagesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        // Strategy 1: try to load as a group ID directly.
        try {
          const data = await getBookingGroup(id!);
          if (!cancelled) {
            setGroupId(data.groupId);
            setGroupDetail(data);
            setSeriesId(seriesIdFromGroup(data.bookings));
          }
          return;
        } catch {
          // Not a group ID — fall through to individual booking lookup.
        }

        // Strategy 2: load the individual booking, then load its group.
        const booking = await getBooking(id!).catch(() => null);
        if (!booking) {
          if (!cancelled) setNotFound(true);
          return;
        }

        const resolvedGroupId: string | null = booking.bookingGroupId ?? null;
        if (!cancelled)
          setSeriesId((booking.recurringSeriesId as string | null | undefined) ?? null);

        if (resolvedGroupId) {
          const data = await getBookingGroup(resolvedGroupId).catch(() => null);
          if (!cancelled) {
            if (data) {
              setGroupId(data.groupId);
              setGroupDetail(data);
            } else {
              // Group load failed but we have the individual booking —
              // synthesise a single-member group so the page still renders.
              setGroupId(resolvedGroupId);
              setGroupDetail({ groupId: resolvedGroupId, bookings: [booking] });
            }
          }
        } else {
          // No group ID — synthesise a single-member group using the booking id itself.
          if (!cancelled) {
            setGroupId(id!);
            setGroupDetail({ groupId: id!, bookings: [booking] });
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          if (e instanceof Error && e.message.toLowerCase().includes('not found')) {
            setNotFound(true);
          } else {
            setError((e as Error).message ?? 'Failed to load booking');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Scroll to messages section after data loads if hash is #messages.
  useEffect(() => {
    if (!loading && groupDetail && shouldScrollMessages.current && messagesRef.current) {
      messagesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, groupDetail]);

  function handleClose() {
    navigate(-1);
  }

  function handleDeleted() {
    navigate('/today');
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Page header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Go back"
            >
              <ArrowLeft className="size-4" strokeWidth={1.75} aria-hidden />
              Back
            </button>
            <h1 className="font-heading text-base font-semibold text-foreground">Booking detail</h1>
            {seriesId && <SeriesChip seriesId={seriesId} />}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 size-5 animate-spin" strokeWidth={1.75} />
            Loading booking…
          </div>
        )}

        {!loading && notFound && (
          <div className="rounded-xl border border-border bg-surface px-6 py-10 text-center">
            <p className="text-sm font-medium text-foreground">Booking not found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              This booking may have been deleted, or you may not have access to it.
            </p>
            <button
              type="button"
              onClick={() => navigate('/today')}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Return to today
            </button>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-6 py-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && !notFound && !error && groupId && (
          <div ref={messagesRef}>
            <BookingDetail
              groupId={groupId}
              groupDetail={groupDetail}
              onClose={handleClose}
              onDeleted={handleDeleted}
            />
          </div>
        )}
      </div>
    </div>
  );
}
